import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, MoreHorizontal, KeyRound, Power, PowerOff, Trash2, RefreshCcw, Link as LinkIcon } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtDateTime } from "@/lib/utils";
import type { Account, UserOut, UserRole } from "@/lib/types";

function accountLabel(a: Account): string {
  if (a.username) return `@${a.username}`;
  return [a.first_name, a.last_name].filter(Boolean).join(" ") || a.phone;
}

function CreateUserDialog({
  open, onOpenChange, onDone,
}: { open: boolean; onOpenChange: (v: boolean) => void; onDone: () => void }) {
  const { push } = useToast();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>("worker");
  const [loading, setLoading] = useState(false);

  async function submit() {
    setLoading(true);
    try {
      await api.createUser({ username: username.trim(), password, role });
      push({ title: "Пользователь создан", variant: "success" });
      onDone();
      onOpenChange(false);
      setUsername(""); setPassword(""); setRole("worker");
    } catch (e) {
      push({ title: "Ошибка", description: e instanceof ApiError ? e.message : String(e), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Новый пользователь</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Логин</Label>
            <Input className="mt-1.5" value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
          </div>
          <div>
            <Label>Пароль</Label>
            <Input className="mt-1.5" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div>
            <Label>Роль</Label>
            <div className="mt-1.5">
              <Select value={role} onValueChange={(v) => setRole(v as UserRole)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="worker">Воркер</SelectItem>
                  <SelectItem value="admin">Админ</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={submit} disabled={loading || !username.trim() || !password} className="w-full">
            {loading ? "Создаю…" : "Создать"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({
  user, onClose, onSave, saving,
}: { user: UserOut | null; onClose: () => void; onSave: (password: string) => void; saving: boolean }) {
  const [password, setPassword] = useState("");
  useEffect(() => { setPassword(""); }, [user?.id]);

  return (
    <Dialog open={!!user} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-xs">
        <DialogHeader>
          <DialogTitle>Новый пароль для {user?.username}</DialogTitle>
        </DialogHeader>
        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Отмена</Button>
          <Button onClick={() => password && onSave(password)} disabled={saving || !password}>
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GrantAccountsDialog({
  user, accounts, onClose,
}: { user: UserOut | null; accounts: Account[]; onClose: () => void }) {
  const { push } = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    api.getUserAccounts(user.id)
      .then((ids) => setSelected(new Set(ids)))
      .catch(() => setSelected(new Set()))
      .finally(() => setLoading(false));
  }, [user?.id]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function save() {
    if (!user) return;
    setSaving(true);
    try {
      await api.setUserAccounts(user.id, Array.from(selected));
      push({ title: "Доступы обновлены", variant: "success" });
      onClose();
    } catch (e) {
      push({ title: "Ошибка", description: e instanceof ApiError ? e.message : String(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={!!user} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Доступ к аккаунтам — {user?.username}</DialogTitle>
        </DialogHeader>
        <div className="max-h-96 space-y-1 overflow-y-auto rounded border border-border">
          {loading && <p className="p-3 text-sm text-muted-foreground">Загрузка…</p>}
          {!loading && accounts.length === 0 && (
            <p className="p-3 text-sm text-muted-foreground">Нет Telegram-аккаунтов.</p>
          )}
          {!loading && accounts.map((a) => (
            <label
              key={a.id}
              className="flex cursor-pointer items-center gap-3 border-b border-border/50 px-3 py-2 text-sm last:border-0 hover:bg-accent/40"
            >
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border bg-input"
                checked={selected.has(a.id)}
                onChange={() => toggle(a.id)}
              />
              <span className="flex-1">{accountLabel(a)}</span>
              <span className="font-mono text-xs text-muted-foreground">{a.phone}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Отмена</Button>
          <Button onClick={save} disabled={saving || loading}>
            {saving ? "Сохраняю…" : "Сохранить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Workers() {
  const qc = useQueryClient();
  const { push } = useToast();
  const [creating, setCreating] = useState(false);
  const [resetUser, setResetUser] = useState<UserOut | null>(null);
  const [grantUser, setGrantUser] = useState<UserOut | null>(null);

  const { data: users = [], refetch, isFetching } = useQuery({
    queryKey: ["users"],
    queryFn: api.listUsers,
  });
  const { data: accounts = [] } = useQuery({
    queryKey: ["accounts"],
    queryFn: api.listAccounts,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });

  function handleError(e: unknown) {
    push({ title: "Ошибка", description: e instanceof ApiError ? e.message : String(e), variant: "destructive" });
  }

  const toggleActive = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) => api.updateUser(id, { is_active }),
    onSuccess: invalidate,
    onError: handleError,
  });
  const resetPassword = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) => api.updateUser(id, { password }),
    onSuccess: () => {
      setResetUser(null);
      push({ title: "Пароль обновлён", variant: "success" });
    },
    onError: handleError,
  });
  const del = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: invalidate,
    onError: handleError,
  });

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <div>
          <h1 className="text-lg font-semibold">Воркеры</h1>
          <p className="text-sm text-muted-foreground">
            {users.length} {users.length === 1 ? "пользователь" : "пользователей"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCcw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            Обновить
          </Button>
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Добавить
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-background/95 backdrop-blur">
            <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-2 text-left font-medium">Логин</th>
              <th className="px-4 py-2 text-left font-medium">Роль</th>
              <th className="px-4 py-2 text-left font-medium">Статус</th>
              <th className="px-4 py-2 text-left font-medium">Создан</th>
              <th className="w-12 px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-border/50 transition-colors hover:bg-accent/40">
                <td className="px-4 py-3 font-medium">{u.username}</td>
                <td className="px-4 py-3">
                  <Badge variant={u.role === "admin" ? "default" : "outline"}>{u.role}</Badge>
                </td>
                <td className="px-4 py-3">
                  {u.is_active ? (
                    <Badge variant="success">активен</Badge>
                  ) : (
                    <Badge variant="destructive">отключён</Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground" title={fmtDateTime(u.created_at)}>
                  {fmtDateTime(u.created_at)}
                </td>
                <td className="px-2 py-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>Действия</DropdownMenuLabel>
                      {u.role === "worker" && (
                        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTimeout(() => setGrantUser(u), 0); }}>
                          <LinkIcon className="h-4 w-4" /> Доступ к аккаунтам
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTimeout(() => setResetUser(u), 0); }}>
                        <KeyRound className="h-4 w-4" /> Сбросить пароль
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleActive.mutate({ id: u.id, is_active: !u.is_active })}>
                        {u.is_active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                        {u.is_active ? "Отключить" : "Включить"}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        destructive
                        onClick={() => {
                          if (confirm(`Удалить пользователя ${u.username}?`)) del.mutate(u.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" /> Удалить
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
            {users.length === 0 && !isFetching && (
              <tr>
                <td colSpan={5} className="px-4 py-16 text-center text-sm text-muted-foreground">
                  Пока ни одного пользователя. Нажми <strong>Добавить</strong>.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CreateUserDialog open={creating} onOpenChange={setCreating} onDone={invalidate} />

      <ResetPasswordDialog
        user={resetUser}
        onClose={() => setResetUser(null)}
        onSave={(password) => resetUser && resetPassword.mutate({ id: resetUser.id, password })}
        saving={resetPassword.isPending}
      />

      <GrantAccountsDialog
        user={grantUser}
        accounts={accounts}
        onClose={() => setGrantUser(null)}
      />
    </div>
  );
}
